import { Fragment, type JSX } from 'react'

import { Column, Grid, Media, Row } from '@syconium/little-miss-figgy'

import { useFixturesContext } from '../../../brunswick/containers/fixtures'
import { socialMediaHandles, tagline } from '../../../constants/figs'
import { useEmailCaptureForm } from '../../../containers/EmailCaptureFormContainer'
import { NavAccordion } from '../NavAccordion'
import { NavListSection } from '../NavListSection'

import * as FooterSubComponents from './components'
import { Body, CaptureWrapper, ResponsiveImage, SectionsWrapper } from './styles'

export const NavFooter = () => {
  const { footer } = useFixturesContext()

  const { hideEmailCaptureForm } = useEmailCaptureForm()
  const desktopSections = footer?.desktopSections || []
  const mobileSections = footer?.mobileSections || []
  const socialLinks = (footer?.socialLinks || []).map(link => ({
    text: link.text || '',
    href: link.href || '',
    icon: (
      <ResponsiveImage
        src={link.icon || ''}
        alt={link.text}
        aspectRatios={{ sm: null, md: null }}
        widths={{ unit: 'px', sm: 20, md: 20 }}
        loading='lazy'
        transparentBackground={true}
      />
    ),
  }))

  const desktopSectionElements = (footer?.desktopSections || []).map((section, index) => {
    return (
      <Column key={index} desktop={12 / desktopSections.length}>
        {renderFooterSection(section)}
      </Column>
    )
  })

  const mobileSectionElements = mobileSections.map((section, index) => {
    return <Fragment key={index}>{renderFooterSection(section)}</Fragment>
  })

  const legalLinks = (footer?.legalLinks || []).map(link => ({
    href: link.href || '',
    title: link.text || '',
  }))

  const promo = (
    <FooterSubComponents.FooterPromo hashtag={socialMediaHandles.hashtag} socialLinks={socialLinks}>
      {tagline}
    </FooterSubComponents.FooterPromo>
  )

  return (
    <>
      {!hideEmailCaptureForm && (
        <CaptureWrapper aria-label='email capture form'>
          <div id='lightbox-inline-form-e2b8cfc0-ee68-498b-9240-53d553d0ac0f'></div>
        </CaptureWrapper>
      )}

      <Body data-testid='site-footer' role='contentinfo'>
        <Media greaterThanOrEqual='md'>
          <Grid>
            <Row>
              <Column desktop={6}>{promo}</Column>
              <Column desktop={6}>
                <SectionsWrapper>{desktopSectionElements}</SectionsWrapper>
              </Column>
            </Row>
          </Grid>
        </Media>
        <Media lessThan='md'>
          {promo}
          {mobileSectionElements}
        </Media>

        <FooterSubComponents.FooterFooter
          asTag='div'
          legalLinks={legalLinks}
          copyright={footer?.copyrightLine || ''}
        />
      </Body>
    </>
  )
}

type FooterSectionData =
  | NonNullable<ReturnType<typeof useFixturesContext>['footer']>['desktopSections'][number]
  | NonNullable<ReturnType<typeof useFixturesContext>['footer']>['mobileSections'][number]

function renderFooterSection(props: FooterSectionData): JSX.Element | null {
  switch (props.__typename) {
    case 'NavAccordion':
      return <NavAccordion {...props} />
    case 'NavListSection':
      return <NavListSection {...props} />
    default:
      return null
  }
}
